import { ArrowRight, ArrowUpRight, Check, MessageCircle } from "lucide-react";
import { BrandMark } from "../components/BrandMark";
import { tenantTheme } from "../lib/branding";

const workflow = [
  [
    "Das Gespräch eröffnen",
    "Ein klarer Einstieg erklärt, worum es heute geht. Dein Team berichtet direkt vom Veranstaltungsort — mit Text, Bildern und den Beobachtungen dazwischen.",
  ],
  [
    "Zuhören und auswählen",
    "Fragen aus dem Publikum landen zuerst bei der Redaktion. Ihr entscheidet, welche Stimmen in eure Berichterstattung gehören.",
  ],
  [
    "Den Moment bewahren",
    "Wer später dazukommt, findet den ganzen Verlauf. Nach dem Event bleibt euer Liveblog als Archiv zum Nachlesen erhalten.",
  ],
];

export function ProductPage() {
  return (
    <div className="forum-product" style={tenantTheme()}>
      <header className="forum-product__nav">
        <a href="#main" aria-label="VZ.live Startseite">
          <BrandMark />
        </a>
        <nav aria-label="Hauptnavigation">
          <a href="#produkt">Das Produkt</a>
          <a href="#liveblog">So sieht es aus</a>
          <a className="forum-button" href="mailto:live@vonzwei.media">
            Projekt anfragen <ArrowUpRight size={18} aria-hidden="true" />
          </a>
        </nav>
      </header>
      <main id="main">
        <section className="forum-hero">
          <div>
            <h1>Aus Zuschauern werden Mitredende.</h1>
            <p>
              Öffne dein Event für die Menschen hinter dem Bildschirm. Berichte
              live, sammle Fragen und gib guten Beiträgen einen Platz in eurer
              Geschichte.
            </p>
            <div className="forum-actions">
              <a
                className="forum-button forum-button--primary"
                href="mailto:live@vonzwei.media"
              >
                Euren Liveblog starten{" "}
                <ArrowUpRight size={18} aria-hidden="true" />
              </a>
              <a
                href="https://demo.live.von2.media/events/stadtlichter"
                target="_blank"
                rel="noopener noreferrer"
              >
                Live-Demo ansehen <ArrowRight size={18} aria-hidden="true" />
              </a>
            </div>
          </div>
          <div className="forum-conversation" aria-hidden="true">
            <span>Was bewegt euch?</span>
            <span>
              Lasst uns darüber reden. <ArrowUpRight size={38} />
            </span>
          </div>
        </section>
        <div className="forum-strip" aria-label="Einsatzorte">
          <span>Auf der Bühne.</span>
          <span>Im Publikum.</span>
          <span>Auf deiner Website.</span>
          <span>Im Gespräch.</span>
        </div>
        <section className="forum-workflow" id="produkt">
          <div className="forum-section-intro">
            <h2>Nähe entsteht, wenn jemand antwortet.</h2>
            <p>
              Ein guter Liveblog ist keine Einbahnstraße. VZ.live verbindet die
              Menschen vor Ort, euren Live-Desk und das Publikum. Ihr gebt dem
              Geschehen einen Zusammenhang.
            </p>
          </div>
          <ol>
            {workflow.map(([title, description], index) => (
              <li key={title}>
                <span aria-hidden="true">0{index + 1}</span>
                <h3>{title}</h3>
                <p>{description}</p>
              </li>
            ))}
          </ol>
        </section>
        <section className="forum-product-example" id="liveblog">
          <div
            className="forum-example"
            aria-label="Illustratives Liveblog-Beispiel, keine Live-Daten"
          >
            <div className="forum-example__header">
              <MessageCircle size={20} aria-hidden="true" />
              <span>Beispiel: Stadtgespräch</span>
            </div>
            <ol>
              <li>
                <time>19:16</time>
                <div>
                  <strong>Lea · Moderation</strong>
                  <p>
                    Welche Orte fehlen euch im Viertel? Wir nehmen eure Fragen
                    mit auf die Bühne.
                  </p>
                </div>
              </li>
              <li>
                <time>19:12</time>
                <div>
                  <strong>Nora · Aus dem Publikum</strong>
                  <p>
                    Ein Ort, an dem man sich ohne Verabredung trifft — das wäre
                    schön.
                  </p>
                  <small>
                    <Check size={14} aria-hidden="true" /> Redaktionell
                    freigegeben
                  </small>
                </div>
              </li>
              <li>
                <time>19:08</time>
                <div>
                  <strong>Lea · Moderation</strong>
                  <p>Die Diskussion beginnt. Schön, dass ihr dabei seid!</p>
                </div>
              </li>
            </ol>
            <p className="forum-example__note">
              So könnte euer Liveblog aussehen.{" "}
              <a
                href="https://demo.live.von2.media/events/stadtlichter"
                target="_blank"
                rel="noopener noreferrer"
              >
                Die interaktive Demo öffnen.
              </a>
            </p>
          </div>
          <div>
            <h2>Eure Website bleibt der Treffpunkt.</h2>
            <p>
              Bindet den Liveblog direkt in einen Artikel ein oder gebt euren
              Events eine eigene Startseite. Eure Farben, euer Logo und eure
              Domain sorgen dafür, dass sich das Publikum gleich zu Hause fühlt.
            </p>
            <a className="forum-text-link" href="mailto:live@vonzwei.media">
              Über euren Einsatz sprechen{" "}
              <ArrowUpRight size={18} aria-hidden="true" />
            </a>
          </div>
        </section>
        <section className="forum-questions">
          <h2>Bevor es losgeht.</h2>
          <div>
            <details>
              <summary>Wie arbeitet unser Team zusammen?</summary>
              <p>
                Im Live-Desk laufen die Beiträge zusammen. Eure Redaktion
                schreibt, prüft Einreichungen und veröffentlicht. Von unterwegs
                können Teammitglieder direkt mit dem Handy berichten.
              </p>
            </details>
            <details>
              <summary>Wie kommt der Ticker auf unsere Website?</summary>
              <p>
                Ein Embed integriert den Verlauf in eure bestehende Seite.
                Zusätzlich kann eure Marke eine eigene Eventübersicht mit
                kommenden Veranstaltungen und Archiv erhalten.
              </p>
            </details>
            <details>
              <summary>Wer entscheidet, was öffentlich wird?</summary>
              <p>
                Ihr behaltet die redaktionelle Kontrolle. Beiträge aus dem
                Publikum werden erst nach eurer Freigabe veröffentlicht. Externe
                Medien werden erst nach Zustimmung geladen.
              </p>
            </details>
          </div>
        </section>
        <section className="forum-closing">
          <h2>Was möchtet ihr ins Gespräch bringen?</h2>
          <a
            className="forum-button forum-button--primary"
            href="mailto:live@vonzwei.media"
          >
            Lasst uns darüber sprechen{" "}
            <ArrowUpRight size={18} aria-hidden="true" />
          </a>
        </section>
      </main>
      <footer className="forum-product__footer">
        <BrandMark />
        <p>Ein Produkt von vonzwei.media</p>
        <a href="/app-datenschutz.html">App-Datenschutz</a>
        <a href="mailto:live@vonzwei.media">live@vonzwei.media</a>
      </footer>
    </div>
  );
}
